import React, { useState, useEffect, useMemo } from 'react';
import MuiSlider from '@mui/material/Slider';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import StopCircleRoundedIcon from '@mui/icons-material/StopCircleRounded';
import './slider_nowcasting.css';
import 'leaflet/dist/leaflet.css';
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew"
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos"
function formatDateForGeoServer(date) {
  // Asegurarse de que los segundos y milisegundos sean cero, conservando los minutos
  date.setUTCSeconds(0, 0);
  return date.toISOString();
}

function formatDateForDisplay(date) {
  const year = date.getUTCFullYear();
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = date.getUTCDate().toString().padStart(2, '0');
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  const seconds = date.getUTCSeconds().toString().padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} UTC`;
}

function TimeSliderNowcasting({ onTimeChange, forecastValue }) {
  // Inicializa el slider en 0 y ajusta la fecha inicial con el desfase de 10 minutos
  const [sliderValue, setSliderValue] = useState(0);
  const [adjustedDateTime, setAdjustedDateTime] = useState(
    formatDateForDisplay(new Date(new Date(forecastValue).setUTCMinutes(new Date(forecastValue).getUTCMinutes() + 10)))
  );
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  // Monitorea el ancho de la ventana para ajustes responsivos
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!forecastValue) return;
    setSliderValue(0);

    const dateWithOffset = new Date(forecastValue);
    dateWithOffset.setUTCMinutes(dateWithOffset.getUTCMinutes() + 10);

    const geoServerDateTime = formatDateForGeoServer(dateWithOffset);
    const display = formatDateForDisplay(dateWithOffset);
    setAdjustedDateTime(display);

    onTimeChange(geoServerDateTime);
  }, [forecastValue]);

  //detener autoplay si el slider es movido manualmente
  const handleChangeCommitted = () => {
    if (isAutoPlay) {
      setIsAutoPlay(false);   // apaga el autoplay
    }
  };

  const handleSliderChange = (_, newValue) => {
    setSliderValue(newValue);

    // Partimos otra vez del forecastValue
    const baseDate = new Date(forecastValue);
    // Sumamos los 10 min de offset inicial
    baseDate.setUTCMinutes(baseDate.getUTCMinutes() + 10);
    // Sumamos 10 min por cada step del slider
    baseDate.setUTCMinutes(baseDate.getUTCMinutes() + (newValue * 10));

    const geoServerDateTime = formatDateForGeoServer(baseDate);
    const displayDateTime = formatDateForDisplay(baseDate);

    setAdjustedDateTime(displayDateTime);
    onTimeChange(geoServerDateTime);  // Notifica al padre
  };

  const handlePlayButtonClick = () => {
    setIsAutoPlay(!isAutoPlay);
  };

  useEffect(() => {
    if (isAutoPlay) {
      const interval = setInterval(() => {
        setSliderValue((prevValue) => {
          const nextValue = prevValue < 18 ? prevValue + 1 : 0;
          // 18 pasos de 10 minutos cubren 3 horas
          handleSliderChange(null, nextValue);
          return nextValue;
        });
      }, 2500);
      return () => clearInterval(interval);
    }
  }, [isAutoPlay, forecastValue]);
  const handleStepBack = () => {
    setSliderValue((prev) => Math.max(0, prev - 1))
    if (isAutoPlay) {
      setIsAutoPlay(false)
    }
  }

  const handleStepForward = () => {
    setSliderValue((prev) => Math.min(18, prev + 1))
    if (isAutoPlay) {
      setIsAutoPlay(false)
    }
  }
  // RESPONSIVIDAD
  // Ancho del slider según el tamaño de pantalla
  const getSliderWidth = useMemo(() => {
    switch (true) {
      case windowWidth <= 275:
        return '50%';
      case windowWidth <= 500:
        return '70%';
      case windowWidth <= 700:
        return '77%';
      case windowWidth <= 900:
        return '84%';
      case windowWidth <= 1100:
        return '83%';
      case windowWidth <= 1500:
        return '88%';
      default:
        return '90%';
    }
  }, [windowWidth]);

  // Posición vertical del thumb del slider
  const getSliderTop = () => {
    if (windowWidth <= 700) return '61%';
    if (windowWidth <= 900) return '58%';
    if (windowWidth <= 1100) return '60%';
    if (windowWidth <= 1200) return '59%';
    if (windowWidth <= 1500) return '52%';
    return '50%';
  };

  // Tamaño de la fuente para el label del valor
  const getValueLabelFontSize = useMemo(() => {
    switch (true) {
      case windowWidth <= 275:
        return '0.65rem';
      case windowWidth <= 500:
        return '0.7rem';
      case windowWidth <= 768:
        return '0.75rem';
      default:
        return '0.875rem';
    }
  }, [windowWidth]);

  // Padding para el label del valor
  const getValueLabelPadding = useMemo(() => {
    switch (true) {
      case windowWidth <= 275:
        return '0.25rem 0.5rem';
      case windowWidth <= 500:
        return '0.3rem 0.6rem';
      case windowWidth <= 768:
        return '0.4rem 0.8rem';
      default:
        return '0.5rem 1rem';
    }
  }, [windowWidth]);

  // Tamaño del ícono del botón (play/stop)
  const getIconSize = useMemo(() => {
    switch (true) {
      case windowWidth <= 275:
        return 20;
      case windowWidth <= 500:
        return 22;
      case windowWidth <= 768:
        return 24;
      default:
        return 28;
    }
  }, [windowWidth]);

  // Posición vertical de las marcas
  const getMarkTop = useMemo(() => {
    switch (true) {
      case windowWidth <= 275:
        return '57%';
      case windowWidth <= 320:
        return '60%';
      case windowWidth <= 350:
        return '60%';
      case windowWidth <= 750:
        return '59%';
      case windowWidth <= 1000:
        return '59%';
      case windowWidth <= 1100:
        return '58%';
      case windowWidth <= 1200:
        return '59%';
      case windowWidth <= 1300:
        return '52%';
      case windowWidth <= 1500:
        return '52%';
      case windowWidth <= 1800:
        return '50%';
      default:
        return '50%';
    }
  }, [windowWidth]);

  // Posición vertical del label de las marcas
  const getMarkLabelTop = useMemo(() => {
    switch (true) {
      case windowWidth <= 275:
        return '33.5px';
      case windowWidth <= 320:
        return '33.5px';
      case windowWidth <= 350:
        return '33.5px';
      case windowWidth <= 750:
        return '33.5px';
      case windowWidth <= 1000:
        return '31px';
      case windowWidth <= 1100:
        return '30.5px';
      case windowWidth <= 1200:
        return '30.55px';
      case windowWidth <= 1300:
        return '-7.5px';
      case windowWidth <= 1500:
        return '-7.5px';
      case windowWidth <= 1800:
        return '-7.5px';
      default:
        return '-7.5px';
    }
  }, [windowWidth]);

  // Generación de marcas responsivas:
  const marks = useMemo(() => {
    // Define qué valores se etiquetan según el ancho
    let labelKeys;
    if (windowWidth < 500) {
      labelKeys = [6, 18];          // 1h y 3h
    } else if (windowWidth < 1000) {
      labelKeys = [6, 12, 18];      // 1h, 2h, 3h
    } else {
      labelKeys = [3, 6, 9, 12, 15, 18]; // +30m, 1h, 1h30m, 2h, 2h30m, 3h
    }
    // Construye el map de etiquetas dinámico
    const labelMap = labelKeys.reduce((map, key) => {
      let label;
      switch (key) {
        case 3: label = '+30m'; break;
        case 6: label = '+1h'; break;
        case 9: label = '+1h30m'; break;
        case 12: label = '+2h'; break;
        case 15: label = '+2h30m'; break;
        case 18: label = '+3h'; break;
        default: label = '';
      }
      map[key] = label;
      return map;
    }, {});
    return Array.from({ length: 19 }, (_, i) => ({
      value: i,
      label: labelMap[i] || ''
    }));
  }, [windowWidth]);

  return (
    <div className="time-slider-container">
      {/* Play/Stop Button */}
      <button
        className={`ts-custom-btn ${isAutoPlay ? 'ts-custom-btn--stop' : 'ts-custom-btn--play'}`}
        onClick={handlePlayButtonClick}
        aria-label={isAutoPlay ? "Stop autoplay" : "Start autoplay"}
      >
        {isAutoPlay ? (
          <StopCircleRoundedIcon />
        ) : (
          <PlayCircleOutlineRoundedIcon />
        )}
      </button>
      {windowWidth <= 768 && (
        <button
          className="ts-nav-btn"
          onClick={handleStepBack}
          aria-label="Step back 1 hour"
        >
          <ArrowBackIosNewIcon />
        </button>
      )}
      <MuiSlider
        className="time-slider-horizontal"
        min={0}
        max={18} // 18 pasos de 10 minutos para 3 horas
        value={sliderValue}
        step={1}
        onChange={handleSliderChange}
        onChangeCommitted={handleChangeCommitted}
        valueLabelDisplay="on"
        valueLabelFormat={(value) => {
          const baseDate = new Date(forecastValue);
          baseDate.setUTCMinutes(baseDate.getUTCMinutes() + 10 + value * 10);
          return formatDateForDisplay(baseDate);
        }}
        marks={marks}
        sx={{
          // Only essential overrides that can't be handled by CSS
          '& .MuiSlider-valueLabel': {
            fontSize: windowWidth <= 350 ? '0.6rem' :
              windowWidth <= 500 ? '0.65rem' :
                windowWidth <= 768 ? '0.7rem' : '0.75rem',
          },
          '& .MuiSlider-markLabel': {
            fontSize: windowWidth <= 350 ? '0.6rem' :
              windowWidth <= 500 ? '0.65rem' :
                windowWidth <= 768 ? '0.7rem' : '0.75rem',
          }
        }}
      />
      {windowWidth <= 768 && (
        <button
          className="ts-nav-btn"
          onClick={handleStepForward}
          aria-label="Step forward 1 hour"
        >
          <ArrowForwardIosIcon />
        </button>
      )}
      {/* Opcional: un dialog o display adicional para adjustedDateTime */}
    </div>
  );
}

export default TimeSliderNowcasting;
